import type { KeyboardEvent, MouseEvent } from "react";
import type { PMIssue } from "@/domains/pm/types";
import { useLanguage } from "@/lib/i18n/LanguageContext";

/**
 * What the List, Kanban and Gantt take to draw the /issues page's drafts —
 * new issues still being written, which have no row in the database yet.
 *
 * A draft is drawn beside the issues, never among them: it is inert (no
 * checkbox, right-click menu, inline editor, drag or link) and no placement,
 * schedule or bulk write ever sees its id. A click hands it back to the page,
 * which opens it in the draft panel.
 */
export interface DraftIssueViewProps {
  /** `issuesStore.draftIssuesFor(buildingId)`: projections with negative ids, newest first. */
  draftIssues?: readonly PMIssue[];
  /** The draft open in the panel — highlighted the way a selected issue is. */
  activeDraftId?: number | null;
  /** Drafts whose save is running: labelled "Saving…" and not clickable. */
  savingDraftIds?: ReadonlySet<number>;
  /** A click on a draft that is not being saved. */
  onSelectDraft?: (draftId: number) => void;
}

const NO_DRAFTS: readonly PMIssue[] = [];

/** The props with their defaults filled in, and the click guard every view needs. */
export function useDraftIssueView({
  draftIssues = NO_DRAFTS,
  activeDraftId = null,
  savingDraftIds,
  onSelectDraft,
}: DraftIssueViewProps) {
  const isSaving = (id: number) => savingDraftIds?.has(id) ?? false;
  return {
    drafts: draftIssues,
    isActive: (id: number) => activeDraftId === id,
    isSaving,
    /** Opens the draft, unless it is being saved — there is nothing to edit then. */
    select: (id: number) => {
      if (!isSaving(id)) onSelectDraft?.(id);
    },
  };
}

/**
 * Click and keyboard props for a draft's row, card or label: the whole thing
 * is one target, so Enter and Space open it as a click does.
 */
export function draftActivationProps(onActivate: () => void, label: string) {
  return {
    role: "button" as const,
    tabIndex: 0,
    "aria-label": label,
    onClick: (e: MouseEvent) => {
      // The second click of a double-click would toggle what the first opened.
      if (e.detail > 1) return;
      onActivate();
    },
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onActivate();
      }
    },
  };
}

/** The accessible name of a draft's row, card or bar: "New: <title>". */
export function useDraftIssueLabel() {
  const { t } = useLanguage();
  return (issue: PMIssue) =>
    `${t.pm.issues.draft.marker}: ${issue.title.trim() || t.pm.issues.draft.untitled}`;
}
